import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from './api';
import LoadingSpinner from './components/LoadingSpinner';

export default function MemberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  const [member, setMember] = useState(null);
  const [forumStats, setForumStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [plugYourWork, setPlugYourWork] = useState([]);
  const [points, setPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingPlugYourWork, setLoadingPlugYourWork] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchMemberProfile = useCallback(async () => {
    try {
      setLoading(true);
      
      const url = `${process.env.REACT_APP_API_URL || '/api'}/members/${id}/profile`;
      
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Member not found');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch member profile');
      }
      
      const result = await response.json();
      setMember(result.member);
      setForumStats(result.forumStats);
      setRecentPosts(result.recentPosts || []);
      setPoints(result.points || 0);
    } catch (err) {
      console.error('❌ Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id, authenticatedFetch]);

  const fetchPlugYourWork = useCallback(async () => {
    try {
      setLoadingPlugYourWork(true);
      const url = `${process.env.REACT_APP_API_URL || '/api'}/plug-your-work?member_id=${id}&limit=5`;
      const response = await authenticatedFetch(url);
      
      if (response.ok) {
        const result = await response.json();
        setPlugYourWork(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching plug your work:', err);
    } finally {
      setLoadingPlugYourWork(false);
    }
  }, [id, authenticatedFetch]);

  // Fetch member profile
  useEffect(() => {
    if (isAuthenticated && id) {
      fetchMemberProfile();
      fetchPlugYourWork();
    }
  }, [isAuthenticated, id, fetchMemberProfile, fetchPlugYourWork]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDisplayName = () => {
    if (member.first_name && member.last_name) {
      return `${member.first_name} ${member.last_name}`;
    }
    if (member.first_name) {
      return member.first_name;
    }
    if (member.last_name) {
      return member.last_name;
    }
    if (member.name) {
      return member.name;
    }
    return member.email || 'Unknown Member';
  };

  const getInitials = () => {
    const name = getDisplayName();
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh'
      }}>
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading || loadingPlugYourWork) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh'
      }}>
        <LoadingSpinner message="Loading member profile..." />
      </div>
    );
  }

  // Remove error display - just show loading spinner and let React Query retry

  if (!member) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Member not found
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button 
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}
        >
          ← Back
        </button>
      </div>

      {/* Profile Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef'
      }}>
        {/* Profile Header */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Avatar */}
          <div style={{
            width: '200px',
            height: '200px',
            borderRadius: '8px',
            backgroundColor: '#38b44a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '3rem',
            flexShrink: 0,
            overflow: 'hidden'
          }}>
            {member.profile_picture ? (
              <img 
                src={`data:${member.profile_picture_type || 'image/jpeg'};base64,${member.profile_picture}`}
                alt={getDisplayName()}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              getInitials()
            )}
          </div>

          {/* Name and Basic Info */}
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#333', 
              marginBottom: '0.5rem'
            }}>
              {getDisplayName()}
            </h1>
            
            {/* Points Display */}
            <Link 
              to="/leaderboard"
              style={{
                display: 'inline-block',
                backgroundColor: '#38b44a',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontSize: '1.1rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                textDecoration: 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d8f3d'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#38b44a'}
            >
              {points} points
            </Link>
            
            {member.joined && (
              <div style={{ 
                fontSize: '1rem', 
                color: '#666',
                marginBottom: '0.5rem',
                display: 'block'
              }}>
                Member since {formatDate(member.joined)}
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {member.bio && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#333',
              marginBottom: '0.75rem'
            }}>
              About
            </h3>
            <div 
              style={{
                fontSize: '1rem',
                lineHeight: '1.6',
                color: '#555'
              }}
              dangerouslySetInnerHTML={{ __html: member.bio }}
            />
          </div>
        )}

        {/* Contact Information */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ 
            fontSize: '1.2rem', 
            fontWeight: '600', 
            color: '#333',
            marginBottom: '0.75rem'
          }}>
            Contact
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {member.contact_email && (
              <div style={{ fontSize: '1rem', color: '#555' }}>
                <strong>Email:</strong> {member.contact_email}
              </div>
            )}
            {member.phone && (
              <div style={{ fontSize: '1rem', color: '#555' }}>
                <strong>Phone:</strong> {member.country_code ? `+${member.country_code} ` : ''}{member.phone}
              </div>
            )}
          </div>
        </div>

        {/* Social Media */}
        {(member.twitter_handle || member.bluesky_handle || member.instagram_handle) && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#333',
              marginBottom: '0.75rem'
            }}>
              Social Media
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {member.twitter_handle && (
                <div style={{ fontSize: '1rem', color: '#555' }}>
                  <strong>X/Twitter:</strong> @{member.twitter_handle.replace(/^@/, '')}
                </div>
              )}
              {member.bluesky_handle && (
                <div style={{ fontSize: '1rem', color: '#555' }}>
                  <strong>Bluesky:</strong> @{member.bluesky_handle.replace(/^@/, '')}
                </div>
              )}
              {member.instagram_handle && (
                <div style={{ fontSize: '1rem', color: '#555' }}>
                  <strong>Instagram:</strong> @{member.instagram_handle.replace(/^@/, '')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Forum Activity */}
        {forumStats && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#333',
              marginBottom: '0.75rem'
            }}>
              Forum Activity
            </h3>
            <div style={{ 
              display: 'flex', 
              gap: '2rem',
              fontSize: '1rem',
              color: '#555'
            }}>
              <div>
                <strong>{forumStats.threads_started}</strong> threads started
              </div>
              <div>
                <strong>{forumStats.total_posts}</strong> total posts
              </div>
            </div>
          </div>
        )}

        {/* Recent Posts */}
        {recentPosts && recentPosts.length > 0 && (
          <div>
            <h3 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#333',
              marginBottom: '0.75rem'
            }}>
              Recent Posts
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {recentPosts.map((post) => (
                <div
                  key={post.id}
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    padding: '1rem',
                    border: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => navigate(`/forum/${post.thread_id}`)}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#e9ecef';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  {/* Post Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#38b44a',
                      textDecoration: 'none'
                    }}>
                      {post.thread_subject}
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#666'
                    }}>
                      {formatDate(post.created_at)}
                    </div>
                  </div>
                  
                  {/* Post Preview */}
                  <div 
                    style={{
                      fontSize: '0.9rem',
                      lineHeight: '1.4',
                      color: '#555',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: post.content.length > 200 
                        ? post.content.substring(0, 200) + '...' 
                        : post.content 
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Published */}
        {plugYourWork && plugYourWork.length > 0 && (
          <div>
            <h3 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#333',
              marginBottom: '0.75rem'
            }}>
              Recently Published
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {plugYourWork.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    padding: '1rem',
                    border: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => window.open(entry.url, '_blank')}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#e9ecef';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  {/* Entry Header */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <div>
                      <h4 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        color: '#333',
                        margin: '0 0 0.25rem 0'
                      }}>
                        {entry.headline}
                      </h4>
                      {entry.publication && (
                        <span style={{
                          fontSize: '0.8rem',
                          color: 'white',
                          fontWeight: '600',
                          backgroundColor: '#38b44a',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          display: 'inline-block'
                        }}>
                          {entry.publication}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#666',
                      textAlign: 'right'
                    }}>
                      {formatDate(entry.posted)}
                    </div>
                  </div>
                  
                  {/* Entry Preview */}
                  {entry.details && (
                    <div 
                      style={{
                        fontSize: '0.9rem',
                        lineHeight: '1.4',
                        color: '#555',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {entry.details.length > 150 
                        ? entry.details.substring(0, 150) + '...' 
                        : entry.details
                      }
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
